import { useEffect, useState } from "react";
import { apiUrl, currentUserId, currentUserQuery } from "./auth";
import { readClient, type FxClient } from "./facturx";

type Contact = { id: number; name: string; role: string; email: string; phone: string };
type FicheContact = { id: number; name: string; email: string; phone: string; facturx_client_id: number };

export function Contacts({ fiche, onClose }: { fiche: FicheContact; onClose?: () => void }) {
  const [rows, setRows] = useState<Contact[]>([]);
  const [client, setClient] = useState<FxClient | null>(null);
  const [name, setName] = useState("");
  const [role, setRole] = useState("");
  const [email, setEmail] = useState("");
  const [phone, setPhone] = useState("");
  const [error, setError] = useState("");
  const linked = fiche.facturx_client_id > 0;

  useEffect(() => {
    let alive = true;
    void load(fiche.id)
      .then((next) => { if (alive) setRows(next); })
      .catch((reason: unknown) => { if (alive) setError(reason instanceof Error ? reason.message : "Contacts indisponibles"); });
    return () => { alive = false; };
  }, [fiche.id]);

  useEffect(() => {
    if (!linked) {
      setClient(null);
      return;
    }
    let alive = true;
    void readClient(fiche.facturx_client_id)
      .then((data) => { if (alive) setClient(data.client); })
      .catch(() => { if (alive) setClient(null); });
    return () => { alive = false; };
  }, [linked, fiche.facturx_client_id]);

  async function send(body: Record<string, unknown>) {
    setError("");
    try {
      setRows(await save(fiche.id, body));
    } catch (reason: unknown) {
      setError(reason instanceof Error ? reason.message : "Enregistrement impossible");
    }
  }

  const person = client?.contacts?.[0];
  const referencedName = [person?.first_name, person?.last_name].map((part) => (part || "").trim()).filter(Boolean).join(" ") || fiche.name;
  const referencedEmail = (client?.email || person?.email || fiche.email || "").trim();
  const referencedPhone = (client?.phone || person?.phone || fiche.phone || "").trim();
  const referenced = linked && (referencedEmail !== "" || referencedPhone !== "" || (person?.first_name || person?.last_name || "").trim() !== "");

  return (
    <section>
      <div className="flex items-center justify-between gap-3">
        <h2 className="text-lg font-semibold">Contacts</h2>
      </div>
      {error ? <p className="mt-2 text-sm text-rose-700">{error}</p> : null}
      {referenced ? (
        <article className="crm-soft mt-4 rounded-2xl px-4 py-3">
          <p className="text-[11px] font-semibold uppercase tracking-[0.14em] text-slate-400">Référencé · Factures</p>
          <p className="mt-1 font-semibold">{referencedName}</p>
          {referencedEmail ? <p className="mt-1 text-sm text-slate-500">{referencedEmail}</p> : null}
          {referencedPhone ? <p className="text-sm text-slate-500">{referencedPhone}</p> : null}
        </article>
      ) : (
        <p className="mt-3 text-sm text-slate-500">{linked ? "Le client Factures n’a pas encore de contact." : "Liez Factures pour reprendre le contact de la fiche."}</p>
      )}
      <div className="mt-5 border-t border-slate-200 pt-4">
        <p className="text-[11px] font-semibold uppercase tracking-[0.14em] text-slate-400">Autres contacts</p>
        {rows.length === 0 ? <p className="mt-2 text-sm text-slate-400">Aucun autre contact.</p> : null}
        <ul className="mt-2 space-y-2">
          {rows.map((row) => (
            <li key={row.id} className="rounded-2xl border border-slate-200 px-4 py-3">
              <div className="flex items-start justify-between gap-3">
                <p className="font-semibold">{row.name}</p>
                <button type="button" className="shrink-0 text-xs font-semibold text-slate-400 hover:text-rose-700" onClick={() => void send({ action: "delete", id: row.id })}>Retirer</button>
              </div>
              {row.role ? <p className="mt-1 text-sm text-slate-500">{row.role}</p> : null}
              {row.email ? <p className="text-sm text-slate-500">{row.email}</p> : null}
              {row.phone ? <p className="text-sm text-slate-500">{row.phone}</p> : null}
            </li>
          ))}
        </ul>
      </div>
      <form
        className="mt-5 grid gap-2 border-t border-slate-200 pt-4"
        onSubmit={(event) => {
          event.preventDefault();
          if (!name.trim()) return;
          void send({ action: "add", name: name.trim(), role, email, phone });
          setName("");
          setRole("");
          setEmail("");
          setPhone("");
        }}
      >
        <p className="text-[11px] font-semibold uppercase tracking-[0.14em] text-slate-400">Nouveau contact</p>
        <input className="crm-field h-10 rounded-xl border px-3 text-sm" value={name} placeholder="Nom" onChange={(event) => setName(event.target.value)} />
        <input className="crm-field h-10 rounded-xl border px-3 text-sm" value={role} placeholder="Fonction" onChange={(event) => setRole(event.target.value)} />
        <input className="crm-field h-10 rounded-xl border px-3 text-sm" value={email} placeholder="E-mail" onChange={(event) => setEmail(event.target.value)} />
        <input className="crm-field h-10 rounded-xl border px-3 text-sm" value={phone} placeholder="Téléphone" onChange={(event) => setPhone(event.target.value)} />
        <button type="submit" className="h-10 justify-self-start rounded-full crm-fill px-4 text-sm font-semibold text-white">Ajouter</button>
      </form>
    </section>
  );
}

async function load(fiche: number): Promise<Contact[]> {
  const query = currentUserQuery();
  const res = await fetch(`${apiUrl("contact.php")}${query}${query ? "&" : "?"}fiche=${fiche}`, {
    credentials: "include",
    cache: "no-store",
    headers: currentUserId() ? { "X-User-Id": currentUserId() } : {},
  });
  const data = (await res.json().catch(() => ({}))) as { success?: boolean; message?: string; contacts?: Contact[] };
  if (!res.ok || data.success === false) throw new Error(data.message || "Contacts indisponibles");
  return data.contacts || [];
}

async function save(fiche: number, body: Record<string, unknown>): Promise<Contact[]> {
  const res = await fetch(apiUrl("contact.php") + currentUserQuery(), {
    method: "POST",
    credentials: "include",
    cache: "no-store",
    headers: { ...(currentUserId() ? { "X-User-Id": currentUserId() } : {}), "Content-Type": "application/json" },
    body: JSON.stringify({ fiche, ...body }),
  });
  const data = (await res.json().catch(() => ({}))) as { success?: boolean; message?: string; contacts?: Contact[] };
  if (!res.ok || data.success === false) throw new Error(data.message || "Enregistrement impossible");
  return data.contacts || [];
}
